import store from "@/store";
import { ApplianceList, ApiError, ApiFridgeError, BeaconLogInfo, AttachmentList, UpdatePageInfo, PantryCameraImage, LoadPantryCameraImage, PantryCameraImageResult } from "@/types/common";
import router from "@/router";
import { SaveApplianceList } from "@/types/search";
import { Option } from "@/types/home";
import { API_STATUS_CODE, API_KP_SERVER_ERROR_CODE } from "@/const/api-const";

/** NativeとのIFを定義（lintエラー回避) */
/* eslint-disable */
declare global {
  interface Window {
    webkit: any;
    KP_setToken: any;
    KP_setApplianceId: any;
    KP_setApiKey: any;
    KP_setAppliance: any;
    KP_debugLog: any;
    KP_setTitle: any;
    KP_CB_getTermId: any;
    KP_CB_getApplianceList: any;
    KP_CB_getSearchAppliance: any;
    KP_CB_setHomeState: any;
    KP_CB_getUserAgent: any;
    KP_CB_setUpdatePage: any;
    KP_sendLogViewed: any;
    KP_sendLogAction: any;
    KP_sendLogAction2: any;
    KP_CB_getAttachmentList: any;
    KP_CB_setUpdatePantryList: any;
    KP_CB_setUpdatePantryDetail: any;
    KP_jumpBannerTapEvent: any;
    KP_CB_jumpBannerTapEvent: any;
    KP_savePantryCameraImage: any;
    KP_loadPantryCameraImage: any;
    KP_CB_loadPantryCameraImage: any;
    KP_saveRecommendFoodUpdateDate: any;
    KP_loadRecommendFoodUpdateDate: any;
    KP_CB_loadRecommendFoodUpdateDate: any;
    KP_getNotificationSetting: any;
    KP_CB_getNotificationSetting: any;
    KP_getProductCommunicationID: any;
    KP_CB_getProductCommunicationID: any;
    KP: any;
  }
}

/** タイトル情報 */
export class TitleInfo {
  // トップ画面表示 true:トップ画面/false:トップ画面以外
  top: boolean;
  // タイトル
  title: string;
  // タイトル行右側のアイコン制御用。オプション。（※1）
  rightIcon: boolean;

  constructor(top: boolean, title: string) {
    this.top = top;
    this.title = title;
    this.rightIcon = false;
  }
}

export class BannerTapInfo {
  transitionTarget: string;
  q: string | null;
  order: string | null;
  modelName: Array<string> | null;
  eoj: string | null;
  constructor(transitionTarget: string, options: Option | null) {
    this.transitionTarget = transitionTarget;
    this.q = null;
    this.order = null;
    this.modelName = null;
    this.eoj = null;
  if (options != null) {
      this.q = options.q;
      this.order = options.order;
      this.modelName = options.modelName;
      this.eoj = options.eoj;
    }
  }
}

export default class NativeClient {

  /** Android端末のUAに含まれる文字列 */
  static UA_ANDROID = "Android";
  /** iOS端末のUAに含まれる文字列 */
  static UA_IOS = "iPhone";
  /** iPad端末(iOS12以前)のUAに含まれる文字列 */
  static UA_IPAD = 'iPad';
  /** iPad端末(iOS13以降)のUAに含まれる文字列 */
  static UA_MACINTOSH = 'Macintosh';

  /**
   * iOSのUAかチェックする
   *
   * @returns {boolean} true: iOS false: iOS以外
   */
  static isIOS(): boolean {
    return navigator.userAgent.includes(NativeClient.UA_IOS) || navigator.userAgent.includes(NativeClient.UA_IPAD) || navigator.userAgent.includes(NativeClient.UA_MACINTOSH);
  }

  /**
   * AndroidのUAかチェックする
   *
   * @returns {boolean} true: Android false: Android以外
   */
  static isAndroid(): boolean {
    return navigator.userAgent.includes(NativeClient.UA_ANDROID);
  }

  /**
   * アクセストークンをNative側に要求する
   */
  static getToken() {
    let promise = new Promise((resolve) => {
      window.KP_setToken = (token: string) => {
        NativeClient.sendDebugLog("Native_Client.ts getToken KP_setToken = " + token);
        store.commit("common/updateToken", token);
        resolve(token);
      };
    });

    if (NativeClient.isIOS()) {
      window.webkit.messageHandlers.KP_getToken.postMessage("");
    } else if (NativeClient.isAndroid()) {
      window.KP.KP_getToken();
    } else {
      store.commit("common/updateToken", process.env.VUE_APP_ACCESS_TOKEN);
      return Promise.resolve(process.env.VUE_APP_ACCESS_TOKEN);
    }
    return promise;
  }

  /**
   * 通信用機器IDをNative側に要求する
   */
  static getApplianceId() {
    let promise = new Promise((resolve) => {
      window.KP_setApplianceId = (applianceId: string) => {
        NativeClient.sendDebugLog("Native_Client.ts getApplianceId KP_setApplianceId");
        if (applianceId == "") {
          store.commit("common/updateApplianceId", null);
        } else {
          store.commit("common/updateApplianceId", applianceId);
        }
        resolve(applianceId);
      };
    });

    if (NativeClient.isIOS()) {
      window.webkit.messageHandlers.KP_getApplianceId.postMessage("");
    } else if (NativeClient.isAndroid()) {
      window.KP.KP_getApplianceId();
    } else {
      store.commit("common/updateApplianceId", process.env.VUE_APP_APPLIANCE_ID);
      return Promise.resolve(process.env.VUE_APP_APPLIANCE_ID);
    }
    return promise;
  }

  /**
   * 食PFのAPIキーをNative側に要求する
   */
  static getApiKey() {
    let promise = new Promise((resolve) => {
      window.KP_setApiKey = (apiKey: string) => {
        NativeClient.sendDebugLog("Native_Client.ts getApiKey KP_setApiKey");
        store.commit("common/updateApiKey", apiKey);
        resolve(apiKey);
      };
    });

    if (NativeClient.isIOS()) {
      window.webkit.messageHandlers.KP_getApiKey.postMessage("");
    } else if (NativeClient.isAndroid()) {
      window.KP.KP_getApiKey();
    } else {
      store.commit("common/updateApiKey", process.env.VUE_APP_API_KEY);
      return Promise.resolve(process.env.VUE_APP_API_KEY);
    }
    return promise;
  }

  /**
   * 家電IDをNative側に要求する
   */
  static getAppliance() {
    let promise = new Promise((resolve) => {
      window.KP_setAppliance = (appliance: string) => {
        NativeClient.sendDebugLog("Native_Client.ts getAppliance KP_setAppliance");
        if (appliance == "") {
          store.commit("common/updateAppliance", null);
        } else {
          store.commit("common/updateAppliance", appliance);
        }
        resolve(appliance);
      };
    });

    if (NativeClient.isIOS()) {
      window.webkit.messageHandlers.KP_getAppliance.postMessage("");
    } else if (NativeClient.isAndroid()) {
      window.KP.KP_getAppliance();
    } else {
      store.commit("common/updateAppliance", process.env.VUE_APP_APPLIANCE);
      return Promise.resolve(process.env.VUE_APP_APPLIANCE);
    }
    return promise;
  }

  /**
   * トークンのリフレッシュをNative側に要求する
   */
  static getRefreshToken() {
    let promise = new Promise((resolve) => {
      window.KP_setToken = (token: string) => {
        NativeClient.sendDebugLog("Native_Client.ts getRefreshToken KP_setToken");
        store.commit("common/updateToken", token);
        resolve(token);
      };
    });

    if (NativeClient.isIOS()) {
      window.webkit.messageHandlers.KP_getRefreshToken.postMessage("");
    } else if (NativeClient.isAndroid()) {
      store.commit("common/updateToken", process.env.VUE_APP_ACCESS_TOKEN);
      return Promise.resolve(process.env.VUE_APP_ACCESS_TOKEN);
    }
    return promise;
  }


  /**
   * ログをNative側に通知する
   */
  static sendDebugLog(param: string) {
    if (process.env.VUE_APP_LOCAL_DEBUG == "true") {
      const currentTime = new Date();
      const currentTimeString = currentTime.toISOString();
      console.log(currentTimeString + " " + "[sendDebugLog] " + param);
    }
    if (NativeClient.isIOS()) {
      window.webkit.messageHandlers.KP_debugLog.postMessage(param);
    } else if (NativeClient.isAndroid()) {
      window.KP.KP_debugLog(param);
    }
  }

  /**
   * ログをNative側に通知する
   */
  static setTitle(titleInfo: TitleInfo) {
    let param = "";
    if (titleInfo != null) {
      param = JSON.stringify(titleInfo);
    }
    NativeClient.sendDebugLog("setTitle : " + param);
    if (NativeClient.isIOS()) {
      window.webkit.messageHandlers.KP_setTitle.postMessage(param);
    } else if (NativeClient.isAndroid()) {
      window.KP.KP_setTitle(param);
    }
  }

  /**
  * ログをNative側に通知する
  */
  static transitionReceipDetail(recipeId: number, type: string) {
    NativeClient.sendDebugLog("transitionReceipDetail Recipe");
    location.href = `native-app://transition-recipe-detail?recipe_id=${recipeId}&type=${type}`;
    // PCからレシピ詳細画面を表示する場合
    if (process.env.VUE_APP_LOCAL_RECIPE_DETAIL_DEBUG == "true") {
      NativeClient.sendDebugLog("transitionReceipDetail VUE_APP_LOCAL_RECIPE_DETAIL_DEBUG enable");
      router.push(`/recipe-detail?type=` + type + `&id=` + recipeId + '&debug=true');
    }
  }

  /**
  * 食材詳細画面を起動する
  */
   static transitionPantryDetail(pantryId: string, type: string, plateValue: string) {
    NativeClient.sendDebugLog("transitionPantryDetail pantry_id=" + pantryId + " type=" + type);
    location.href = `native-app://transition-pantry-detail?pantry_id=${pantryId}&type=${type}&plate_value=${plateValue}`;
    // PCから食材詳細画面を表示する場合
    if (process.env.VUE_APP_LOCAL_RECIPE_DETAIL_DEBUG == "true") {
      NativeClient.sendDebugLog("transitionPantryDetail VUE_APP_LOCAL_RECIPE_DETAIL_DEBUG enable");
      router.push(`/pantry-detail?type=` + type + `&id=` + pantryId + "&plateValue=" + plateValue);
    }
  }

  /**
  * 食材PR画面を起動する
  */
   static transitionPantryPRFood(foodId: string) {
    NativeClient.sendDebugLog("transitionPantryDetail food_id=" + foodId);
    location.href = `native-app://transition-pantry-pr-food?food_id=${foodId}`;
    // PCから食材詳細画面を表示する場合
    if (process.env.VUE_APP_LOCAL_RECIPE_DETAIL_DEBUG == "true") {
      NativeClient.sendDebugLog("transitionPantryPRFood VUE_APP_LOCAL_RECIPE_DETAIL_DEBUG enable");
      router.push(`/pantry-pr-food?foodId=` + foodId);
    }
  }

  /**
  * パントリーカメラ画面を起動する
  */
  static transitionPantryCamera() {
    NativeClient.sendDebugLog("transitionPantryCamera");
    location.href = `native-app://transition-pantry-camera`;
    // PCから食材詳細画面を表示する場合
    if (process.env.VUE_APP_LOCAL_RECIPE_DETAIL_DEBUG == "true") {
      NativeClient.sendDebugLog("transitionPantryCamera VUE_APP_LOCAL_RECIPE_DETAIL_DEBUG enable");
      // デバッグ用にCamera用
      const applianceId = process.env.VUE_APP_DEBUG_CAMERA_APPLIANCE_ID;
      if (applianceId != undefined) router.push(`/pantry-camera?applianceId=` + encodeURIComponent(applianceId));
    }
  }

  /**
  * カメラ画面拡大画面を起動する
  */
  static transitionPantryZoom(fileName: string) {
    NativeClient.sendDebugLog("transitionPantryZoom fileName=" + fileName);
    const encodeFileName = encodeURIComponent(fileName);
    NativeClient.sendDebugLog("transitionPantryZoom encode fileName=" + encodeFileName);
    location.href = `native-app://transition-pantry-camera-zoom?file_name=${encodeFileName}`;
    // PCから食材詳細画面を表示する場合
    if (process.env.VUE_APP_LOCAL_RECIPE_DETAIL_DEBUG == "true") {
      NativeClient.sendDebugLog("transitionPantryZoom VUE_APP_LOCAL_RECIPE_DETAIL_DEBUG enable");
      router.push(`/pantry-camera-zoom?fileName=` + encodeFileName);
    }
  }

  /**
  * パントリー食材追加画面を起動する
  */
  static transitionPantryFoodAdd() {
    NativeClient.sendDebugLog("transitionPantryFoodAdd");
    location.href = `native-app://transition-pantry-food-add`;
    // PCから食材詳細画面を表示する場合
    if (process.env.VUE_APP_LOCAL_RECIPE_DETAIL_DEBUG == "true") {
      NativeClient.sendDebugLog("transitionPantryFoodAdd VUE_APP_LOCAL_RECIPE_DETAIL_DEBUG enable");
      router.push(`/pantry-add`);
    }
  }

  /**
  * パントリー使い忘れチェック画面を起動する
  */
   static transitionPantryUsedUpCheck() {
    NativeClient.sendDebugLog("transitionPantryUsedUpCheck");
    location.href = `native-app://transition-pantry-used-up-check`;
    // PCから食材詳細画面を表示する場合
    if (process.env.VUE_APP_LOCAL_RECIPE_DETAIL_DEBUG == "true") {
      NativeClient.sendDebugLog("transitionPantryUsedUpCheck VUE_APP_LOCAL_RECIPE_DETAIL_DEBUG enable");
      router.push(`/pantry-used-up-check`);
    }
  }

  /**
  * 重量検知プレート食材更新
  */
   static updatePlateValue(pantryPlateId: number, applianceId: string) {
    NativeClient.sendDebugLog("updatePlateValue pantryPlateId=" + pantryPlateId + " applianceId=" + applianceId);
    const encode = encodeURIComponent(applianceId)
    location.href = `native-app://update-pantry-plate-food?pantry_plate_id=${pantryPlateId}&appliance_id=${encode}`;
  }

  /**
  * 重量検知プレート食材変更
  */
  static changePlateFood(applianceId: string) {
    NativeClient.sendDebugLog("changePlateFood applianceId=" + applianceId);
    const encode = encodeURIComponent(applianceId)
    location.href = `native-app://change-pantry-plate-food?appliance_id=${encode}`;
  }
  
  /**
  * 重量検知プレート食材変更
  */
   static changePlateFoodReset(applianceId: string) {
    NativeClient.sendDebugLog("changePlateFoodReset applianceId=" + applianceId);
    const encode = encodeURIComponent(applianceId)
    location.href = `native-app://change-pantry-plate-food-reset?appliance_id=${encode}`;
  }

  /**
  * 家電一覧タブを起動する
  */
  static transitionApplianceList() {
    NativeClient.sendDebugLog("transitionApplianceList");
    location.href = "native-app://jump-kaden";
  }


  /**
  * 買い物リストを起動する
  */
  static transitionShoppingList() {
    NativeClient.sendDebugLog("transitionShoppingList");
    location.href = "native-app://shopping-list";
  }

  /**
  * 画面終了
  */
  static pageClose() {
    NativeClient.sendDebugLog("pageClose");
    location.href = "native-app://page-close";
    if (!NativeClient.isIOS() && !NativeClient.isAndroid()) {
      router.go(-1);
    }
  }

  /**
  * 全連携家電一覧をNative側に要求する
  */
  static getApplianceList() {
    let promise = new Promise((resolve) => {
      window.KP_CB_getApplianceList = (appliances: string) => {
        NativeClient.sendDebugLog("Native_Client.ts test getApplianceList KP_CB_getApplianceList = " + appliances);
        let applianceList = new ApplianceList();
        try {
          applianceList = JSON.parse(appliances);
        } catch (e) {
          NativeClient.sendDebugLog("KP_CB_getApplianceList e" + JSON.stringify(e));
        }
        store.commit("common/updateApplianceList", applianceList);
        resolve(appliances);
      };
    });

    if (NativeClient.isIOS()) {
      window.webkit.messageHandlers.KP_getApplianceList.postMessage("");
    } else if (NativeClient.isAndroid()) {
      window.KP.KP_getApplianceList();
    } else {
      let applianceList = new ApplianceList();
      try {
        const param = process.env.VUE_APP_APPLIANCE_LIST;
        if (param !== undefined) {
          applianceList = JSON.parse(param);
        }
      } catch (e) {
        NativeClient.sendDebugLog("KP_CB_getApplianceList e" + JSON.stringify(e));
      }
      store.commit("common/updateApplianceList", applianceList);
      return Promise.resolve(process.env.VUE_APP_APPLIANCE_LIST);
    }
    return promise;
  }

  /**
  * UserAgent（の一部）を取得する
  */
  static getUserAgent() {
    let promise = new Promise((resolve) => {
      window.KP_CB_getUserAgent = (userAgent: string) => {
        NativeClient.sendDebugLog("Native_Client.ts getUserAgent KP_CB_getUserAgent = " + userAgent);
        store.commit("common/updateUserAgent", userAgent);
        resolve(userAgent);
      };
    });

    if (NativeClient.isIOS()) {
      window.webkit.messageHandlers.KP_getUserAgent.postMessage("");
    } else if (NativeClient.isAndroid()) {
      window.KP.KP_getUserAgent();
    } else {
      store.commit("common/updateUserAgent", process.env.VUE_APP_USER_AGENT);
      return Promise.resolve(process.env.VUE_APP_USER_AGENT);
    }
    return promise;
  }

  /**
   * 端末IDをNative側に要求する
   */
  static getTermId() {
    let promise = new Promise((resolve) => {
      window.KP_CB_getTermId = (termId: string) => {
        NativeClient.sendDebugLog("Native_Client.ts getTermId KP_CB_getTermId = " + termId);
        store.commit("common/updateTermId", termId);
        resolve(termId);
      };
    });
  
    if (NativeClient.isIOS()) {
      window.webkit.messageHandlers.KP_getTermId.postMessage("");
    } else if (NativeClient.isAndroid()) {
      window.KP.KP_getTermId();
    } else {
      store.commit("common/updateTermId", process.env.VUE_APP_TERM_ID);
      return Promise.resolve(process.env.VUE_APP_TERM_ID);
    }
    return promise;
  }

  /**
  * API関連のパラメータを取得する（同期処理）
  */
  static getApiParams() {
    return new Promise((resolve) => {
      NativeClient.sendDebugLog("NATIVE_CLIENT.ts getApiParams start");
      Promise.all([
        NativeClient.getToken(),
        NativeClient.getApiKey(),
        NativeClient.getApplianceId(),
        NativeClient.getAppliance(),
        NativeClient.getApplianceList(),
        NativeClient.getUserAgent(),
        NativeClient.getTermId()
      ]).then((result) => {
        // 各パラメータの要求メソッドで保存しているため、ここは、ログを出力しておく
        NativeClient.sendDebugLog("NATIVE_CLIENT.ts getApiParams completed");
        NativeClient.sendDebugLog("NATIVE_CLIENT.ts getApiParams completed result[0] = " + result[0]);
        NativeClient.sendDebugLog("NATIVE_CLIENT.ts getApiParams completed result[1] = " + result[1]);
        NativeClient.sendDebugLog("NATIVE_CLIENT.ts getApiParams completed result[2] = " + result[2]);
        NativeClient.sendDebugLog("NATIVE_CLIENT.ts getApiParams completed result[3] = " + result[3]);
        NativeClient.sendDebugLog("NATIVE_CLIENT.ts getApiParams completed result[4] = " + result[4]);
        NativeClient.sendDebugLog("NATIVE_CLIENT.ts getApiParams completed result[5] = " + result[5]);
        NativeClient.sendDebugLog("NATIVE_CLIENT.ts getApiParams completed result[6] = " + result[6]);
        resolve(true);
      });
    });
  }

  /**
   * レシピ検索用機器情報設定
   */
  static setSearchAppliance(list: SaveApplianceList) {
    let param = "";
    if (list != null) {
      param = JSON.stringify(list);
    }
    this.sendDebugLog("setSearchAppliance : " + param);
    if (NativeClient.isIOS()) {
      window.webkit.messageHandlers.KP_setSearchAppliance.postMessage(param);
    } else if (NativeClient.isAndroid()) {
      window.KP.KP_setSearchAppliance(param);
    }
  }

  /**
   * レシピ検索用機器情報取得
   */
  static getSearchAppliance() {
    let promise = new Promise((resolve) => {
      window.KP_CB_getSearchAppliance = (appliance: string) => {
        this.sendDebugLog("KP_CB_getSearchAppliance appliance=" + appliance);
        store.commit("common/updateSearchAppliance", appliance);
        resolve(appliance);
      };
    });

    this.sendDebugLog("getSearchAppliance");
    if (NativeClient.isIOS()) {
      window.webkit.messageHandlers.KP_getSearchAppliance.postMessage("");
    } else if (NativeClient.isAndroid()) {
      window.KP.KP_getSearchAppliance("");
    } else {
      window.KP_CB_getSearchAppliance(process.env.VUE_APP_SEARCH_APPLIANCE);
    }
    return promise;
  }

  /**
  * Homeの状態変更を登録する
  */
  static setHomeState(listner: () => void) {
    window.KP_CB_setHomeState = () => {
      NativeClient.sendDebugLog("KP_CB_setHomeState call");
      listner();
    };
  }

  /**
  * パントリー一覧の画面更新を登録する
  */
  static updatePantryList(listner: () => void) {
    window.KP_CB_setUpdatePantryList = () => {
      NativeClient.sendDebugLog("KP_CB_setUpdatePantryList call");
      listner();
    };
  }

  /**
  * パントリー詳細の画面更新を登録する
  */
  static updatePantryDetail(listner: () => void) {
    window.KP_CB_setUpdatePantryDetail = () => {
      NativeClient.sendDebugLog("KP_CB_setUpdatePantryDetail call");
      listner();
    };
  }
  
  /**
  * エラー登録する
  */
  static setAPIErrorState(statusCode: number, responseData: ApiError) {
    let argument = "";
    NativeClient.sendDebugLog("KP_setProtectionStatus statusCode = " + statusCode);

    if (statusCode == API_STATUS_CODE.SERVICE_UNAVAILABLE) {
      argument = JSON.stringify({ kind: 1, message: responseData.error?.message });
    } else if (responseData.error != null && responseData.error?.code == API_KP_SERVER_ERROR_CODE.FORCE_VERSION_UP) {
      argument = JSON.stringify({ kind: 0, message: responseData.error?.message });
    }
    if (argument != "") {
      NativeClient.sendDebugLog("KP_setProtectionStatus call argumant = " + argument);
      if (NativeClient.isIOS()) {
        window.webkit.messageHandlers.KP_setProtectionStatus.postMessage(argument);
      } else if (NativeClient.isAndroid()) {
        window.KP.KP_setProtectionStatus(argument);
      } else {
        NativeClient.sendDebugLog("KP_setProtectionStatus local");
      }
      return true;
    }
    NativeClient.sendDebugLog("KP_setProtectionStatus not call");
    return false;
  }

  /**
  * マイリストの更新処理
  */

  static registUpdatePageListener(listener: (parameter: any) => void) {
    window.KP_CB_setUpdatePage = (update: string) => {
      NativeClient.sendDebugLog("Native_Client.ts KP_CB_setUpdatePage parameter = " + update);
      let updatePageInfo = new UpdatePageInfo();
      try {
        updatePageInfo = JSON.parse(update);
        listener(updatePageInfo);
      } catch (e) {
        NativeClient.sendDebugLog("Native_Client.ts KP_CB_setUpdatePage e:" + e);
      }
    };
  }

  /**
  * マイリストの更新要求
  */
  static requestReloadMylist() {
    if (NativeClient.isIOS()) {
      window.webkit.messageHandlers.KP_reload.postMessage("MYLIST");
    } else if (NativeClient.isAndroid()) {
      window.KP.KP_reload("MYLIST");
    } else {
      NativeClient.sendDebugLog("Native_Client.ts KP_reload(\"MYLIST\")");
    }
  }

  /**
  * お気に入り食材リストの更新要求
  */
   static requestReloadFavoriteFood() {
    NativeClient.sendDebugLog("Native_Client.ts requestReloadFavoriteFood");
    if (NativeClient.isIOS()) {
      window.webkit.messageHandlers.KP_reload.postMessage("FAVORITE_FOOD");
    } else if (NativeClient.isAndroid()) {
      window.KP.KP_reload("FAVORITE_FOOD");
    } else {
      NativeClient.sendDebugLog("Native_Client.ts KP_reload(\"FAVORITE_FOOD\")");
    }
  }

  /**
  * ローディング画面の要求
  */
 static requestDisplayLoading(isLoading: boolean) {
  if (NativeClient.isIOS()) {
    window.webkit.messageHandlers.KP_requestLoading.postMessage(isLoading);
  } else if (NativeClient.isAndroid()) {
    window.KP.KP_requestLoading(isLoading);
  } else {
    // ローカル
    NativeClient.sendDebugLog("Native_Client.ts KP_requestLoading( " + isLoading + " )");
    if (isLoading) {
      store.dispatch("common/startLoading");
    } else {
      store.dispatch("common/stopLoading");
    }
  }
 }

  /**
  * パントリー一覧画面更新要求
  */
  static requestUpdatePantryList() {
    NativeClient.sendDebugLog("Native_Client.ts requestUpdatePantryList");
    if (NativeClient.isIOS()) {
      window.webkit.messageHandlers.KP_updatePantryList.postMessage("");
    } else if (NativeClient.isAndroid()) {
      window.KP.KP_updatePantryList();
    } else {
      NativeClient.sendDebugLog("Native_Client.ts KP_updatePantryList");
    }
  }

  /**
  * パントリー一覧画面更新要求
  */
  static requestUpdatePantryListFoodManagement() {
    NativeClient.sendDebugLog("Native_Client.ts requestUpdatePantryListFoodManagement");
    if (NativeClient.isIOS()) {
      window.webkit.messageHandlers.KP_updatePantryListFoodManagement.postMessage("");
    } else if (NativeClient.isAndroid()) {
      window.KP.KP_updatePantryListFoodManagement();
    } else {
      NativeClient.sendDebugLog("Native_Client.ts KP_updatePantryListFoodManagement");
    }
  }

  /**
  * closeボタン表示設定
  */
  static showCloseButton(show: boolean) {
    NativeClient.sendDebugLog("Native_Client.ts showCloseButton show=" + show);
    if (NativeClient.isIOS()) {
      window.webkit.messageHandlers.KP_showCloseButton.postMessage(show);
    } else if (NativeClient.isAndroid()) {
      window.KP.KP_showCloseButton(show);
    } else {
      NativeClient.sendDebugLog("Native_Client.ts KP_showCloseButton");
    }
  }

  /**
  * 庫内画像用closeボタン表示設定
  */
   static showCameraCloseButton(show: boolean) {
    NativeClient.sendDebugLog("Native_Client.ts showCameraCloseButton show=" + show);
    if (NativeClient.isIOS()) {
      window.webkit.messageHandlers.KP_showCameraCloseButton.postMessage(show);
    } else if (NativeClient.isAndroid()) {
      window.KP.KP_showCameraCloseButton(show);
    } else {
      NativeClient.sendDebugLog("Native_Client.ts KP_showCameraCloseButton");
    }
  }


  /**
  * PullToRefresh有効無効設定
  */
  static enablePullToRefresh(enabled: boolean) {
    NativeClient.sendDebugLog("Native_Client.ts enablePullToRefresh enabled=" + enabled);
    if (NativeClient.isIOS()) {
      window.webkit.messageHandlers.KP_enablePullToRefresh.postMessage(enabled);
    } else if (NativeClient.isAndroid()) {
      window.KP.KP_enablePullToRefresh(enabled);
    } else {
      NativeClient.sendDebugLog("Native_Client.ts enablePullToRefresh");
    }
  }

  /**
  * Beaconログ送信 viewedWeb
  */
  static sendViewedLog(logInfo: BeaconLogInfo) {
    NativeClient.sendDebugLog("NativeClient sendViewedLog");
    let requestString = `"page_id":"${logInfo.pageId}"`;
    if (logInfo.objectId != null) {
      requestString = requestString + `,"object_id":"${logInfo.objectId}"`
    } else {
      requestString = requestString + `,"object_id":""`
    }
    if (logInfo.viewedItems.length > 0 ) {
      let tempString = "";
      for (const item of logInfo.viewedItems) {
        tempString = tempString + `${item},`;
      }
      tempString = tempString.slice(0, -1);
      requestString = requestString + `,"viewed_items":"${tempString}"`;
    } else {
      requestString = requestString + `,"viewed_items":""`;
    }
    const actualRequestString = `{${requestString}}`;
    NativeClient.sendDebugLog("NativeClient sendViewedLog request = " + actualRequestString);
    if (NativeClient.isIOS()) {
      window.webkit.messageHandlers.KP_sendLogViewed.postMessage(actualRequestString);
    } else if (NativeClient.isAndroid()) {
      window.KP.KP_sendLogViewed(actualRequestString);
    }
  }
  
  /**
  * Beaconログ送信 clicked_action
  */
  static sendClickedActionLog(logInfo: BeaconLogInfo) {
    NativeClient.sendDebugLog("NativeClient sendClickedActionLog");
    let requestString = `"page_id":"${logInfo.pageId}"`;
    if (logInfo.objectId != null) {
      requestString = requestString + `,"object_id":"${logInfo.objectId}"`
    }
    if (logInfo.viewedItems.length > 0) {
      let tempString = "";
      for (const item of logInfo.viewedItems) {
        tempString = tempString + `${item},`;
      }
      tempString = tempString.slice(0, -1);
      NativeClient.sendDebugLog("sendClickedActionLog items=" + tempString);
      requestString = requestString + `,"viewed_items":"${tempString}"`;
    }
    const actualRequestString = `{${requestString}}`;
    NativeClient.sendDebugLog("NativeClient sendClickedActionLog request = " + actualRequestString);
    if (NativeClient.isIOS()) {
      window.webkit.messageHandlers.KP_sendLogAction.postMessage(actualRequestString);
    } else if (NativeClient.isAndroid()) {
      window.KP.KP_sendLogAction(actualRequestString);
    }
  }

  /**
  * Beaconログ送信 clicked_action viewedItemsを「,」毎に分解しない
  */
   static sendClickedActionLogViewedItemSingleLine(logInfo: BeaconLogInfo) {
    NativeClient.sendDebugLog("NativeClient sendClickedActionLogViewedItemSingleLine");
    let requestString = `"page_id":"${logInfo.pageId}"`;
    if (logInfo.objectId != null) {
      requestString = requestString + `,"object_id":"${logInfo.objectId}"`
    }
    if (logInfo.viewedItems.length > 0) {
      let tempString = "";
      for (const item of logInfo.viewedItems) {
        tempString = tempString + `${item},`;
      }
      tempString = tempString.slice(0, -1);
      requestString = requestString + `,"viewed_items":"{${tempString}}"`;
    }
    const actualRequestString = `{${requestString}}`;
    NativeClient.sendDebugLog("NativeClient sendClickedActionLogViewedItemSingleLine request = " + actualRequestString);
    if (NativeClient.isIOS()) {
      window.webkit.messageHandlers.KP_sendLogAction2.postMessage(actualRequestString);
    } else if (NativeClient.isAndroid()) {
      window.KP.KP_sendLogAction2(actualRequestString);
    }
  }

  /*
  * ApplianceIdに該当するアタッチメントリストを取得する
  */
  static getAttachmentList(applianceId: string) {
    let promise = new Promise((resolve) => {
      window.KP_CB_getAttachmentList = (attachment: string) => {
        NativeClient.sendDebugLog("Native_Client.ts getAttachmentList KP_CB_getAttachmentList = " + attachment);
        let attachmentList = new AttachmentList();
        try {
          attachmentList = JSON.parse(attachment);
        } catch (e) {
          NativeClient.sendDebugLog("KP_CB_getAttachmentList e:" + e);
        }
        NativeClient.sendDebugLog("getAttachmentList　attachmentList" + JSON.stringify(attachmentList));
        store.commit("common/updateAttachmentList", attachmentList);
        resolve(attachment);
      };
    });
    if (NativeClient.isIOS()) {
      window.webkit.messageHandlers.KP_getAttachmentList.postMessage(applianceId);
    } else if (NativeClient.isAndroid()) {
      window.KP.KP_getAttachmentList(applianceId);
    } else {
      let attachmentList = new AttachmentList();
      try {
        const param = process.env.VUE_APP_ATTACHMENT_LIST;
        if (param !== undefined) {
          attachmentList = JSON.parse(param);
        }
      } catch (e) {
        NativeClient.sendDebugLog("getAttachmentList e:" + e);
      }
      NativeClient.sendDebugLog("getAttachmentList　attachmentList" + JSON.stringify(attachmentList));
      store.commit("common/updateAttachmentList", attachmentList);
      return Promise.resolve(process.env.VUE_APP_ATTACHMENT_LIST);
    }
    return promise;
  }

  /*
  * 家電TOP更新要求
  */
  static updateApplianceTop() {
    NativeClient.sendDebugLog("NativeClient updateApplianceTop");
    if (NativeClient.isIOS()) {
      NativeClient.sendDebugLog("NativeClient updateApplianceTop for iOS");
      window.webkit.messageHandlers.KP_updateKadenTop.postMessage("");
    } else if (NativeClient.isAndroid()) {
      NativeClient.sendDebugLog("NativeClient updateApplianceTop for Android");
      window.KP.KP_updateKadenTop();
    } else {
      NativeClient.sendDebugLog("NativeClient updateApplianceTop Non Target");
    }
  }

  /**
  * Beaconログ送信 viewedWeb
  */
  static jumpBannerTapEvent(event: BannerTapInfo) {
    let promise = new Promise((resolve) => {
      window.KP_CB_jumpBannerTapEvent = (sceneChange: boolean) => {
        NativeClient.sendDebugLog("Native_Client.ts jumpBannerTapEvent KP_CB_jumpBannerTapEvent sceneChange = " + sceneChange);
        resolve(sceneChange);
      };
    });
    NativeClient.sendDebugLog("NativeClient jumpBannerTapEvent BannerTapInfo event=%s" + JSON.stringify(event));
    const eventString = JSON.stringify(event);
    NativeClient.sendDebugLog("NativeClient jumpBannerTapEvent eventString = " + eventString);
    if (NativeClient.isIOS()) {
      window.webkit.messageHandlers.KP_jumpBannerTapEvent.postMessage(eventString);
    } else if (NativeClient.isAndroid()) {
      window.KP.KP_jumpBannerTapEvent(eventString);
    } else {
      return Promise.resolve(true);
    }
    return promise;
  }

  /**
  * パントリーカメラ画像保存
  */
  static savePantryCameraImage(info: PantryCameraImage) {
    NativeClient.sendDebugLog("Native_Client.ts savePantryCameraImage fileName=" + info.fileName);
    info.fileName = encodeURIComponent(info.fileName);
    NativeClient.sendDebugLog("Native_Client.ts savePantryCameraImage encode fileName=" + info.fileName);
    const param = JSON.stringify(info);
    try {
      if (NativeClient.isIOS()) {
        window.webkit.messageHandlers.KP_savePantryCameraImage.postMessage(param);
      } else if (NativeClient.isAndroid()) {
        window.KP.KP_savePantryCameraImage(param);
      } else {
        NativeClient.sendDebugLog("Native_Client.ts KP_savePantryCameraImage");
      }
    } catch(e) {
      NativeClient.sendDebugLog("KP_savePantryCameraImage e:" + e);
    }
  }

  /**
  * パントリーカメラ画像取得
  */
  static loadPantryCameraImage(info: LoadPantryCameraImage) {
    let promise = new Promise((resolve) => {
      window.KP_CB_loadPantryCameraImage = (param: string) => {
        NativeClient.sendDebugLog("Native_Client.ts loadPantryCameraImage KP_CB_loadPantryCameraImage param = " + param);
        let result = new PantryCameraImageResult(info.fileName);
        try {
          if (param != "") {
            result = JSON.parse(param);
            if (result && result.fileName) {
              result.fileName = decodeURIComponent(result.fileName);
            }
          }
        } catch (e) {
          NativeClient.sendDebugLog("KP_CB_loadPantryCameraImage e:" + e);
        }
        store.commit("common/updatePantryCameraImage", result);
        resolve(result);
      };
    });
    NativeClient.sendDebugLog("NativeClient loadPantryCameraImage info=%s" + JSON.stringify(info));
    info.fileName = encodeURIComponent(info.fileName);
    NativeClient.sendDebugLog("NativeClient loadPantryCameraImage encode info=%s" + JSON.stringify(info));
    const param = JSON.stringify(info);
    NativeClient.sendDebugLog("NativeClient loadPantryCameraImage param = " + param);
    try {
      if (NativeClient.isIOS()) {
        window.webkit.messageHandlers.KP_loadPantryCameraImage.postMessage(param);
      } else if (NativeClient.isAndroid()) {
        window.KP.KP_loadPantryCameraImage(param);
      } else {
        const result = new PantryCameraImage(info.fileName, null);
        store.commit("common/updatePantryCameraImage", result);
        return Promise.resolve(result);
      }
    } catch (e) {
      NativeClient.sendDebugLog("KP_CB_loadPantryCameraImage e:" + e);
      const result = new PantryCameraImage(info.fileName, null);
      store.commit("common/updatePantryCameraImage", result);
      return Promise.resolve(result);
    }
    return promise;
  }

  /**
  * おすすめ食材の更新日保存要求
  */
  static requestSaveUpdateDateRecommendFoods(dateString: string) {
    NativeClient.sendDebugLog("NativeClient requestSaveUpdateDateRecommendFoods date = " + dateString);
    if (NativeClient.isIOS()) {
      window.webkit.messageHandlers.KP_saveRecommendFoodUpdateDate.postMessage(dateString);
    } else if (NativeClient.isAndroid()) {
      window.KP.KP_saveRecommendFoodUpdateDate(dateString);
    } else {
      NativeClient.sendDebugLog("NativeClient requestSaveUpdateDateRecommendFoods no relevent OS");
    }
  }

  /**
  * おすすめ食材の更新日取得要求
  */
  static getUpdateDateRecommendFoods() {
    NativeClient.sendDebugLog("NativeClient getUpdateDateRecommendFoods");
    let promise = new Promise((resolve) => {
      if (NativeClient.isIOS() || NativeClient.isAndroid()) {
        let received = false;
        window.KP_CB_loadRecommendFoodUpdateDate = (update: string) => {
          received = true;
          NativeClient.sendDebugLog("Native_Client.ts getUpdateDateRecommendFoods KP_CB_loadRecommendFoodUpdateDate param = " + update);
          resolve(update);
        };
        // Nativeで、KP_CB_loadRecommendFoodUpdateDateが実行されなかった場合の対策
        window.setTimeout(() => {
          if (!received) {
            NativeClient.sendDebugLog("Native_Client.ts getUpdateDateRecommendFoods KP_CB_loadRecommendFoodUpdateDate timeout");
            resolve("");
          }
        }, 1000);
      } else {
        // ローカル実行
        const updateDate = process.env.VUE_APP_RECOMMENDATION_FOODS_UPDATE_DATE;
        NativeClient.sendDebugLog("NativeClient getUpdateDateRecommendFoods local updateDate = " + updateDate);
        if (updateDate != undefined) {
          resolve(updateDate);
        } else {
          resolve("");
        }
      }
    });

    if (NativeClient.isIOS()) {
      window.webkit.messageHandlers.KP_loadRecommendFoodUpdateDate.postMessage("");
    } else if (NativeClient.isAndroid()) {
      window.KP.KP_loadRecommendFoodUpdateDate();
    } else {
      NativeClient.sendDebugLog("NativeClient requestLoadUpdateDateRecommendFoods no relevent OS");
    }
    return promise;
  }

  /**
  * 通知設定取得IF
  */
  static requestGetNotificationSetting() {
    NativeClient.sendDebugLog("NativeClient requestGetNotificationSetting");
    if (NativeClient.isIOS()) {
      window.webkit.messageHandlers.KP_getNotificationSetting.postMessage("");
    } else if (NativeClient.isAndroid()) {
      window.KP.KP_getNotificationSetting();
    } else {
      NativeClient.sendDebugLog("NativeClient requestGetNotificationSetting no relevent OS");
    }
  }

  /**
  * 通知設定取得コールバックIF
  */
  static registGetNotificationSettingListener(listener: (parameter: any) => void) {
    window.KP_CB_getNotificationSetting = (value: boolean) => {
      NativeClient.sendDebugLog("Native_Client.ts KP_CB_getNotificationSetting parameter = " + value);
      listener(value);
    };
  }

  /**
  * 冷蔵庫通信ID取得IF
  */
  static getProductCommunicationID() {
    NativeClient.sendDebugLog("NativeClient getProductCommunicationID");
    let promise = new Promise((resolve) => {
      if (NativeClient.isIOS() || NativeClient.isAndroid()) {
        let received = false;
        window.KP_CB_getProductCommunicationID = (communicationId: string) => {
          received = true;
          NativeClient.sendDebugLog("Native_Client.ts getProductCommunicationID KP_CB_getProductCommunicationID param = " + communicationId);
          resolve(communicationId);
        };
        // Nativeで、KP_CB_loadRecommendFoodUpdateDateが実行されなかった場合の対策
        window.setTimeout(() => {
          if (!received) {
            NativeClient.sendDebugLog("Native_Client.ts getProductCommunicationID KP_CB_getProductCommunicationID timeout");
            resolve("");
          }
        }, 1000);
      } else {
        // ローカル実行
        const communicationId = process.env.VUE_APP_PRODUCT_COMMUNICATION_ID;
        NativeClient.sendDebugLog("NativeClient getProductCommunicationID local communicationId = " + communicationId);
        if (communicationId != undefined) {
          resolve(communicationId);
        } else {
          resolve("");
        }
      }
    });

    if (NativeClient.isIOS()) {
      window.webkit.messageHandlers.KP_getProductCommunicationID.postMessage("");
    } else if (NativeClient.isAndroid()) {
      window.KP.KP_getProductCommunicationID();
    } else {
      NativeClient.sendDebugLog("NativeClient getProductCommunicationID no relevent OS");
    }
    return promise;
  }
}
